/**
 * @file
 * Renders one modern SRP filter into a plugin `data-type="custom"` container.
 *
 * The inventory plugin still owns the search: it fetches results, updates the
 * URL and resets dependent filters (category > make > model > trim). These
 * controls only draw the options and hand the chosen value back to the plugin's
 * Custom filter through the container's `handleChangeEvent` hook.
 */

import { formatPrice } from '../../utilities.js';
import {
  colourSwatch,
  escapeHtml,
  formatNumber,
  isSelected,
  LOCKED_PARAMS,
  optionKey,
  optionLabel,
  splitList,
  sprintf,
} from './utils.js';

export const CHANGE_EVENT = 'srp-filter:change';

const RANGE_PARAMS = ['price', 'year', 'odometer'];

// Search settings the plugin sends with every search; they aren't filters.
const SEARCH_SETTINGS = ['drilldown_metadata', 'metadata', 'limit', 'offset', 'fields', 'lang'];

// Room around the API's price range for incentive adjustments (see renderPriceRange).
const PRICE_PAD_BELOW = { ratio: 0.15, min: 5000 };
const PRICE_PAD_ABOVE = { ratio: 0.05, min: 2500 };
const ODOMETER_STEPS = [25000, 50000, 75000, 100000, 150000, 200000];

// The Deals filter spans three API parameters instead of one.
export const DEAL_PARAMS = ['on_sale', 'price_modifier', 'has_discounted_price'];

// Matches the h-5/w-5 thumb in filters-modern.css.
const THUMB_SIZE = 20;

// Ticking options waits this long for more ticks in the same filter, so a
// quick run of clicks sends one search instead of one per click. The plugin
// doesn't cancel a search when another starts, so this also stops results
// from arriving out of order.
const COMMIT_DELAY = 300;

// A typed price searches once typing has paused this long (or on Enter or
// leaving the box), so a half-typed "4" for $45,000 doesn't search on its own.
const PRICE_TYPING_DELAY = 2000;

/** The one change waiting to be sent: { control, detail, key, timer }. */
let pending = null;

/**
 * Send the waiting change now, if there is one.
 */
export function flushPendingChange() {
  if (!pending) {
    return;
  }
  const { control, detail, timer } = pending;
  window.clearTimeout(timer);
  pending = null;
  control.send(detail);
}

/**
 * Parameters chosen but not sent yet, so a redraw from an earlier search
 * doesn't undo ticks the visitor has just made.
 */
export function pendingParams() {
  if (!pending) {
    return {};
  }
  const { control, detail } = pending;
  if (RANGE_PARAMS.includes(control.param)) {
    return {
      [`${control.param}_min`]: detail.min ?? undefined,
      [`${control.param}_max`]: detail.max ?? undefined,
    };
  }
  return { [detail.param ?? control.param]: detail.value ?? '' };
}

const CHEVRON = `<svg class="srp-options__chevron" viewBox="0 0 20 20" aria-hidden="true">
  <path d="M5.5 7.5 10 12l4.5-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

export class FilterControl {
  /**
   * @param {HTMLElement} container  the `.filter-container` element
   * @param {Object}      i18n       translated strings from the template
   * @param {Function}    onCommit   called with (control, detail, previous) after a change
   */
  constructor(container, i18n, onCommit) {
    this.container = container;
    this.section = container.closest('[data-filter-section]');
    this.clearButton = this.section?.querySelector('.srp-filter__clear') ?? null;
    this.param = container.dataset.searchParameter ?? container.id;
    this.control = container.dataset.control ?? 'checkbox';
    this.label = container.dataset.label ?? this.param;
    this.visible = Number.parseInt(container.dataset.visible ?? '7', 10);
    this.sort = container.dataset.sort ?? 'count';
    this.searchAfter = Number.parseInt(container.dataset.searchAfter ?? '12', 10);
    this.step = Number.parseInt(container.dataset.step ?? '1', 10);
    // "unfiltered": the API counts this facet across the whole site, ignoring
    // the other filters (Body Style today, CarpagesSearchBundle#79), so the filter
    // hides once any other filter is set rather than offer choices that may not match.
    this.countsUnfiltered = container.dataset.counts === 'unfiltered';
    // Vehicles the current search found (set by index.js), to check those counts.
    this.resultTotal = null;
    this.reveal = this.section?.dataset.reveal ?? '';
    this.i18n = i18n;
    this.onCommit = onCommit;

    this.expanded = false;
    this.query = '';
    this.bounds = null;
    this.histogram = null;
    this.snapshot = null;
    this.facet = undefined;
    this.dealCounts = {};
    this.pluginFilter = null;
    this.data = {};
    this.params = {};

    // Trim sits inside the chosen Model, and Packages inside the chosen Trim.
    this.nest = this.section?.dataset.nest ?? '';
    this.nestHint = this.section?.dataset.nestHint ?? '';
    this.parentControl = null;
    this.children = [];
    this.nestedBlock = null;
    this.placed = false;

    this.bindPlugin();
    this.bindEvents();
  }

  /**
   * Elements of this filter only, leaving out any filter nested inside it.
   */
  ownAll(selector) {
    return Array.from(this.container.querySelectorAll(selector)).filter((el) => this.owns(el));
  }

  own(selector) {
    return this.ownAll(selector)[0] ?? null;
  }

  owns(el) {
    return el.closest('.filter-container') === this.container;
  }

  /**
   * Show this filter inside the parent's chosen option instead of in its own
   * section. Its container keeps its id, so the plugin still drives it.
   */
  nestUnder(parent) {
    this.parentControl = parent;
    parent.children.push(this);

    this.nestedBlock = document.createElement('div');
    this.nestedBlock.className = 'srp-nested';
    this.nestedBlock.setAttribute('role', 'group');
    this.nestedBlock.setAttribute('aria-label', this.label);
    this.container.before(this.nestedBlock);
    this.nestedBlock.append(this.container);
    this.home = this.nestedBlock.parentElement;
    if (this.section) {
      this.section.hidden = true;
    }
  }

  /**
   * The rows of this filter that are ticked. A nested filter that isn't on
   * show has none, so its own children stay hidden too.
   */
  checkedRows() {
    if (this.parentControl && !this.placed) {
      return [];
    }
    return this.ownAll('.srp-options__item').filter(
      (item) => item.querySelector('.srp-option__input')?.checked,
    );
  }

  /**
   * Move each nested filter under the one ticked option, or back to its hidden
   * section when none or several are ticked. Several ticked shows a hint, as
   * trims and packages only make sense for one model or trim at a time.
   */
  placeChildren(focusKeys = []) {
    if (!this.children.length) {
      return;
    }
    const rows = this.checkedRows();
    const list = this.own('.srp-options');

    this.children.forEach((child, index) => {
      const hasOptions = child.optionCount >= child.minOptions || child.hasValue();
      const target = rows.length === 1 && hasOptions ? rows[0] : child.home;
      if (child.nestedBlock.parentElement !== target) {
        target.append(child.nestedBlock);
      }
      child.placed = target !== child.home;

      const hintClass = `srp-nested__hint--${child.param}`;
      const hint = list?.querySelector(`.${hintClass}`);
      if (rows.length > 1 && hasOptions && child.nestHint) {
        if (!hint) {
          list.insertAdjacentHTML(
            'beforeend',
            `<p class="srp-nested__hint ${hintClass}">${escapeHtml(child.nestHint)}</p>`,
          );
        }
      } else {
        hint?.remove();
      }

      child.placeChildren();
      child.restoreFocus(focusKeys[index]);
    });
  }

  get isDeals() {
    return this.control === 'deals';
  }

  /**
   * The URL parameters this filter owns.
   */
  get ownKeys() {
    if (RANGE_PARAMS.includes(this.param)) {
      return [`${this.param}_min`, `${this.param}_max`];
    }
    return this.isDeals ? DEAL_PARAMS : [this.param];
  }

  /**
   * Parameters that don't change this filter's counts: its own, and those of
   * the filters nested under it, so F-150 keeps its count and place in Model
   * when one of its trims is ticked.
   */
  /**
   * Whether this filter's counts can be trusted for the current search.
   */
  showCounts() {
    if (!this.countsUnfiltered) {
      return true;
    }
    // They're right when they add up to the vehicles found: nothing else has
    // narrowed the search, even if the page pins a value (used inventory pins the
    // dealer's locations).
    const total = this.facetTotal();
    if (total !== null && total === this.resultTotal) {
      return true;
    }
    // The site-wide counts are still scoped to the page (new / used, dealer),
    // so those parameters don't make them wrong, and neither do the plugin's own
    // search settings, which come with the first page load.
    const ignored = [
      ...this.facetKeys,
      ...LOCKED_PARAMS,
      ...SEARCH_SETTINGS,
      'custom_filter',
      'sort',
      'page',
      'paged',
    ];
    return !Object.keys(this.params).some((key) => this.params[key] && !ignored.includes(key));
  }

  /**
   * Vehicles counted in this filter's latest data: the top level of a tree like
   * Body Style ("Passenger Vehicle"), or every option of a flat list.
   */
  facetTotal() {
    const raw = this.data[this.param];
    if (!Array.isArray(raw) || !raw.length) {
      return null;
    }
    const hasLevels = raw.some((option) => option.level !== undefined);
    return raw
      .filter((option) => !hasLevels || option.level === 0)
      .reduce((sum, option) => sum + (Number(option.doc_count) || 0), 0);
  }

  /**
   * Choices a filter needs before it shows. One choice can't narrow anything
   * (every vehicle left already has it), so lists need two. Price is a range,
   * and a filter with filters nested in it (Model > Trim) stays so they can show.
   */
  get minOptions() {
    return this.control === 'range' || this.children.length ? 1 : 2;
  }

  get facetKeys() {
    return this.ownKeys.concat(...this.children.map((child) => child.facetKeys));
  }

  /**
   * Let the plugin's Custom filter call back into this control. The Custom
   * filter only binds its listener once, so `filter` is always that first
   * instance, which is what `updateCallback` expects as `this` for ranges.
   */
  bindPlugin() {
    this.container.handleChangeEvent = (eventName, event, filter) => {
      if (eventName !== CHANGE_EVENT || !event.detail) {
        return;
      }
      this.pluginFilter = filter;
      this.commit(event.detail);
    };
  }

  bindEvents() {
    // Keep native change events inside the container. The plugin listens for
    // CHANGE_EVENT instead, and the legacy layout's analytics handler would
    // otherwise misread these labels.
    this.container.addEventListener('change', (event) => {
      event.stopPropagation();
      this.handleInput(event.target);
    });

    // A nested filter's events also bubble through its parent's container.
    this.container.addEventListener('input', (event) => {
      if (!this.owns(event.target)) {
        return;
      }
      if (event.target.matches('.srp-options__search')) {
        this.query = event.target.value;
        this.filterOptionList();
      } else if (event.target.matches('.srp-range__thumb')) {
        this.syncRange(event.target);
      } else if (event.target.matches('.srp-range__number')) {
        this.previewPriceInputs();
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.commitPriceInputs(), PRICE_TYPING_DELAY);
      }
    });

    // Select the whole price on focus, so typing replaces it.
    this.container.addEventListener('focusin', (event) => {
      if (this.owns(event.target) && event.target.matches('.srp-range__number')) {
        event.target.select();
      }
    });

    this.container.addEventListener('keydown', (event) => {
      if (!this.owns(event.target)) {
        return;
      }
      if (event.key === 'Enter' && event.target.matches('.srp-range__number')) {
        event.preventDefault();
        this.commitPriceInputs();
      }
    });

    this.container.addEventListener('click', (event) => {
      if (!this.owns(event.target)) {
        return;
      }
      const more = event.target.closest('.srp-options__more');
      if (more) {
        this.expanded = !this.expanded;
        this.render(this.data, this.params);
        // Move focus to the first option the click revealed.
        const next = this.expanded
          ? this.own('.srp-options__item.is-extra .srp-option__input')
          : this.own('.srp-options__more');
        next?.focus();
      }
    });

    this.clearButton?.addEventListener('click', () => this.clear());
  }

  /**
   * Draw the control from the latest inventory data and search parameters.
   */
  render(data, params) {
    this.data = data ?? {};
    this.params = params ?? {};
    this.facet = this.getFacet();

    // Redrawing the list drops any filter nested in it, focus and all.
    const focusKey = this.getFocusKey();
    const childFocusKeys = this.children.map((child) => child.getFocusKey());

    switch (this.control) {
      case 'range':
        this.renderPriceRange();
        break;
      case 'year-list':
        this.renderYearList();
        break;
      case 'year-chips':
        this.renderYearChips();
        break;
      case 'year-range':
        this.renderYearRange();
        break;
      case 'odometer':
        this.renderOdometer();
        break;
      case 'deals':
        this.renderDeals();
        break;
      case 'radio':
      case 'icons':
      case 'checkbox':
      default:
        this.renderOptionList();
        break;
    }

    this.restoreFocus(focusKey);
    this.container.removeAttribute('aria-busy');
    this.updateSection();
    this.placeChildren(childFocusKeys);
    this.parentControl?.placeChildren();
  }

  /**
   * This filter's slice of the inventory data.
   *
   * The API narrows some facets by their own selection (pick "XLT" and trim
   * only offers XLT), which makes multi-select impossible. When the other
   * filters are unchanged since this one was last empty, the facet from that
   * response is exactly the "everything except me" list, so reuse it.
   */
  getFacet() {
    const ownKeys = this.facetKeys;
    const context = JSON.stringify(
      Object.keys(this.params)
        .filter((key) => !ownKeys.includes(key) && key !== 'sort' && this.params[key])
        .sort()
        .map((key) => [key, String(this.params[key])]),
    );
    const facet = this.data[this.isDeals ? 'price_modifier' : this.param];

    if (!this.hasValue()) {
      this.snapshot = { context, facet };
      return facet;
    }

    return this.snapshot?.context === context && this.snapshot.facet ? this.snapshot.facet : facet;
  }

  /*
  |--------------------------------------------------------------------------
  | Option lists (checkbox / radio / icons)
  |--------------------------------------------------------------------------
  */

  getOptions() {
    const raw = this.facet;
    if (!Array.isArray(raw)) {
      return [];
    }

    // Category comes back as a tree; the top level ("Passenger Vehicle") is a
    // grouping that cannot be filtered on by itself.
    const hasLevels = raw.some((option) => option.level !== undefined);
    const selected = this.getSelected();

    const options = raw.filter((option) => {
      if (hasLevels && option.level === 0) {
        return false;
      }
      // Selections go to the plugin and API as a comma-separated list, so a value
      // with a comma in it (Ford's interior colours, e.g. "BLACK, UNIQUE SPORT
      // CLOTH...") would be split into two values and match the wrong vehicles.
      if (String(optionKey(this.param, option)).includes(',')) {
        return false;
      }
      return option.doc_count > 0 || isSelected(this.param, option, selected);
    });

    // Keep every selected value listed so it can be unticked, even once other
    // filters leave it with no matches.
    selected.forEach((value) => {
      if (!options.some((option) => isSelected(this.param, option, [value]))) {
        options.push({ slug: value, name: value.replace(/-/g, ' '), doc_count: 0 });
      }
    });

    if (this.sort === 'count') {
      options.sort(
        (a, b) =>
          (b.doc_count ?? 0) - (a.doc_count ?? 0) ||
          optionLabel(this.param, a).localeCompare(optionLabel(this.param, b)),
      );
    } else if (this.sort === 'name') {
      options.sort((a, b) =>
        optionLabel(this.param, a).localeCompare(optionLabel(this.param, b), undefined, {
          numeric: true,
        }),
      );
    }

    return options;
  }

  getSelected() {
    return splitList(this.params[this.isDeals ? 'price_modifier' : this.param]);
  }

  renderOptionList() {
    const options = this.getOptions();
    const selected = this.getSelected();
    const isRadio = this.control === 'radio';
    const inputType = isRadio ? 'radio' : 'checkbox';
    const name = `srp_${this.param}`;

    this.optionCount = options.length;

    if (!options.length) {
      this.container.innerHTML = '';
      return;
    }

    const rows = options.map((option) => ({
      key: optionKey(this.param, option),
      label: optionLabel(this.param, option),
      count: this.showCounts() ? option.doc_count : undefined,
      checked: isSelected(this.param, option, selected),
    }));

    let html = '<div class="srp-options">';

    if (options.length > this.searchAfter) {
      html += `<input type="search" class="srp-options__search" value="${escapeHtml(this.query)}"
        placeholder="${escapeHtml(sprintf(this.i18n.search, this.label))}"
        aria-label="${escapeHtml(sprintf(this.i18n.search, this.label))}">`;
    }

    if (isRadio) {
      rows.unshift({ key: '', label: this.i18n.any, checked: !selected.length });
    }

    html += this.optionListHtml(rows, { inputType, name });
    html += '</div>';
    this.container.innerHTML = html;

    if (this.query) {
      this.filterOptionList();
    }
  }

  /**
   * A list of option rows, trimmed to the most popular few with a See more
   * toggle. Selected rows always stay visible.
   */
  optionListHtml(rows, { inputType, name }) {
    const collapsed = !this.expanded && rows.length > this.visible + 1;
    let hiddenCount = 0;

    const items = rows
      .map((row, index) => {
        const extra = index >= this.visible;
        const hidden = collapsed && extra && !row.checked;
        hiddenCount += hidden ? 1 : 0;
        return this.optionRow({ inputType, name, ...row, hidden, extra });
      })
      .join('');

    const modifier = this.control === 'icons' ? ' srp-options__list--icons' : '';
    let html = `<ul class="srp-options__list${modifier}" role="list">${items}</ul>`;

    if (hiddenCount > 0 || (this.expanded && rows.length > this.visible + 1)) {
      const text = this.expanded ? this.i18n.seeLess : this.i18n.seeMore;
      html += `<button type="button" class="srp-options__more${this.expanded ? ' is-expanded' : ''}"
        aria-expanded="${this.expanded}">${CHEVRON}<span>${escapeHtml(text)}</span></button>`;
    }

    return html;
  }

  optionRow({ inputType, name, key, label, count, checked, hidden, extra, attrs = '' }) {
    let visual = '';
    if (this.param === 'ext_colour' || this.param === 'int_colour') {
      const swatch = colourSwatch(label);
      visual = `<span class="srp-option__swatch${swatch ? '' : ' srp-option__swatch--unknown'}"
        ${swatch ? `style="background-color:${swatch.hex}"` : ''} aria-hidden="true"></span>`;
    }

    const classes = ['srp-options__item', extra ? 'is-extra' : ''].join(' ').trim();

    return `<li class="${classes}"${hidden ? ' hidden' : ''} data-label="${escapeHtml(label.toLowerCase())}">
      <label class="srp-option">
        <input type="${inputType}" class="srp-option__input" name="${escapeHtml(name)}"
          value="${escapeHtml(key)}"${checked ? ' checked' : ''} ${attrs}>
        ${visual}
        <span class="srp-option__label">${escapeHtml(label)}</span>
        ${count !== undefined && count !== null ? `<span class="srp-option__count">${formatNumber(count)}</span>` : ''}
      </label>
    </li>`;
  }

  /**
   * Narrow a long list as the visitor types, ignoring the See more cut-off.
   */
  filterOptionList() {
    const query = this.query.trim().toLowerCase();
    const items = this.ownAll('.srp-options__item');
    const more = this.own('.srp-options__more');

    items.forEach((item) => {
      if (query) {
        item.dataset.wasHidden ??= item.hidden ? '1' : '';
        item.hidden = !item.dataset.label.includes(query);
      } else if (item.dataset.wasHidden !== undefined) {
        item.hidden = item.dataset.wasHidden === '1';
        delete item.dataset.wasHidden;
      }
    });

    if (more) {
      more.hidden = Boolean(query);
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Deals & savings
  |--------------------------------------------------------------------------
  |
  | "Sale price" (on_sale), each named price modifier such as "Employee
  | Pricing" (price_modifier, once the API returns that facet), and "Any
  | savings" (has_discounted_price). Each option sets its own parameter, so
  | ticking two narrows to vehicles that have both.
  |
  */

  setDealCounts(counts) {
    this.dealCounts = counts ?? {};
    if (this.isDeals) {
      this.render(this.data, this.params);
    }
  }

  dealRows() {
    const rows = [];
    const counts = this.dealCounts;
    const flag = (param, label) => {
      const checked = Boolean(this.params[param]);
      const count = counts[param];
      if (checked || count === undefined || count > 0) {
        rows.push({ key: 'true', label, count, checked, attrs: `data-param="${param}"` });
      }
    };

    flag('on_sale', this.i18n.salePrice);

    const selected = this.getSelected();
    this.getOptions().forEach((option) => {
      rows.push({
        key: optionKey('price_modifier', option),
        label: optionLabel('price_modifier', option),
        count: option.doc_count,
        checked: isSelected('price_modifier', option, selected),
        attrs: 'data-param="price_modifier"',
      });
    });

    flag('has_discounted_price', this.i18n.anySavings);

    return rows;
  }

  renderDeals() {
    const rows = this.dealRows();
    this.optionCount = rows.length;

    if (!rows.length) {
      this.container.innerHTML = '';
      return;
    }

    this.container.innerHTML = `<div class="srp-options">
      ${this.optionListHtml(rows, { inputType: 'checkbox', name: 'srp_deals' })}
    </div>`;
  }

  /*
  |--------------------------------------------------------------------------
  | Ranges
  |--------------------------------------------------------------------------
  */

  /**
   * Price: a histogram slider plus min/max inputs with Go.
   *
   * The bounds follow the results (pick a model and they narrow to its
   * prices). Once a price is chosen the API narrows `price.min/max` to it, so
   * from then on the widest bounds seen are kept; otherwise the handles could
   * never be dragged back out again.
   */
  renderPriceRange() {
    const price = this.facet;
    if (!price || price.min === undefined || price.max === undefined) {
      this.container.innerHTML = '';
      this.optionCount = 0;
      return;
    }

    const step = this.step || 1000;
    // The API's prices are before the OEM incentives the plugin takes off the
    // cards (a $41,612 truck can show as $36,112), so leave room below the
    // lowest price, and a little above the highest, for those adjustments.
    const padBelow = Math.max(PRICE_PAD_BELOW.min, price.min * PRICE_PAD_BELOW.ratio);
    const padAbove = Math.max(PRICE_PAD_ABOVE.min, price.max * PRICE_PAD_ABOVE.ratio);
    const min = Math.max(0, Math.floor((price.min - padBelow) / step) * step);
    const max = Math.ceil((price.max + padAbove) / step) * step;
    const hasPriceFilter = Boolean(this.params.price_min || this.params.price_max);

    this.bounds =
      hasPriceFilter && this.bounds
        ? { min: Math.min(this.bounds.min, min), max: Math.max(this.bounds.max, max) }
        : { min, max };

    // A deep link can carry a price outside the narrowed bounds the API returns.
    if (Number(this.params.price_min)) {
      this.bounds.min = Math.min(this.bounds.min, Number(this.params.price_min));
    }
    if (Number(this.params.price_max)) {
      this.bounds.max = Math.max(this.bounds.max, Number(this.params.price_max));
    }

    if (!hasPriceFilter || !this.histogram || price === this.snapshot?.facet) {
      this.histogram = Array.isArray(price.ranges) ? price.ranges : [];
    }

    // When every vehicle left has the same price (often just one vehicle) the
    // slider can't narrow anything, so Price hides like a list with one choice.
    // The padding above would still give it a range of its own.
    const narrows = price.max > price.min || hasPriceFilter;
    this.optionCount = narrows && this.bounds.max > this.bounds.min ? 1 : 0;
    if (!this.optionCount) {
      this.container.innerHTML = '';
      return;
    }

    const low = clamp(Number(this.params.price_min) || this.bounds.min, this.bounds);
    const high = clamp(Number(this.params.price_max) || this.bounds.max, this.bounds);

    // A search can redraw this while a price box has focus; put the cursor back.
    const focused = this.container.contains(document.activeElement)
      ? document.activeElement.dataset.bound
      : null;
    const priceBox = (bound, value, label) => `<label class="srp-range__field">
          <span class="srp-range__field-label">${escapeHtml(label)}</span>
          <span class="srp-range__box">
            <span class="srp-range__currency" aria-hidden="true">$</span>
            <input type="text" inputmode="numeric" autocomplete="off" class="srp-range__number"
              data-bound="${bound}" value="${formatNumber(value)}"
              aria-label="${escapeHtml(bound === 'min' ? this.i18n.minPrice : this.i18n.maxPrice)}">
          </span>
        </label>`;

    this.container.innerHTML = `<div class="srp-range" data-min="${this.bounds.min}" data-max="${this.bounds.max}">
      ${this.histogramHtml(low, high)}
      <div class="srp-range__track">
        <div class="srp-range__fill"></div>
        <input type="range" class="srp-range__thumb srp-range__thumb--min" data-bound="min"
          min="${this.bounds.min}" max="${this.bounds.max}" step="${step}" value="${low}"
          aria-label="${escapeHtml(this.i18n.minPrice)}">
        <input type="range" class="srp-range__thumb srp-range__thumb--max" data-bound="max"
          min="${this.bounds.min}" max="${this.bounds.max}" step="${step}" value="${high}"
          aria-label="${escapeHtml(this.i18n.maxPrice)}">
      </div>
      <div class="srp-range__inputs">
        ${priceBox('min', low, this.i18n.min)}
        <span class="srp-range__dash" aria-hidden="true">&ndash;</span>
        ${priceBox('max', high, this.i18n.max)}
      </div>
    </div>`;

    this.syncRange();

    if (focused) {
      const input = this.container.querySelector(`.srp-range__number[data-bound="${focused}"]`);
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    }
  }

  /**
   * Split the stock into about three price bands of similar size, on the
   * histogram's bucket edges so every count is exact.
   */
  getPriceBands() {
    const buckets = this.histogram
      .map((range) => ({ to: range.to ?? null, count: range.doc_count ?? 0 }))
      .filter((bucket) => bucket.count > 0);
    const total = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
    if (total < 4 || buckets.length < 2) {
      return [];
    }

    const cuts = [];
    let running = 0;
    buckets.forEach((bucket) => {
      running += bucket.count;
      const target = (cuts.length + 1) / 3;
      if (bucket.to && running < total && running / total >= target - 0.08 && cuts.length < 2) {
        cuts.push({ at: bucket.to, below: running });
      }
    });

    const bands = [];
    let from = null;
    let counted = 0;
    cuts.forEach((cut) => {
      bands.push({ min: from, max: cut.at, count: cut.below - counted });
      from = cut.at;
      counted = cut.below;
    });
    bands.push({ min: from, max: null, count: total - counted });

    return bands.filter((band) => band.count > 0);
  }

  histogramHtml(low, high) {
    const { min, max } = this.bounds;
    const bars = this.histogram
      .map((range) => ({
        from: range.from ?? min,
        to: range.to ?? max,
        count: range.doc_count ?? 0,
      }))
      .filter((bar) => bar.to > min && bar.from < max);

    const peak = Math.max(...bars.map((bar) => bar.count), 0);
    if (bars.length < 2 || !peak) {
      return '';
    }

    const html = bars
      .map((bar) => {
        const height = bar.count ? Math.max(8, Math.round((bar.count / peak) * 100)) : 0;
        const inRange = bar.to > low && bar.from < high;
        return `<span class="srp-range__bar${inRange ? ' is-in-range' : ''}" style="height:${height}%"
          data-from="${bar.from}" data-to="${bar.to}"></span>`;
      })
      .join('');

    return `<div class="srp-range__histogram" aria-hidden="true">${html}</div>`;
  }

  /**
   * Keep the two thumbs, the filled track, the number inputs and the histogram
   * in step while a thumb is being dragged.
   */
  syncRange(active, { keepInputs = false } = {}) {
    const range = this.container.querySelector('.srp-range');
    if (!range) {
      return;
    }

    const minThumb = range.querySelector('.srp-range__thumb--min');
    const maxThumb = range.querySelector('.srp-range__thumb--max');
    let low = Number(minThumb.value);
    let high = Number(maxThumb.value);

    if (low > high) {
      if (active === minThumb) {
        low = high;
        minThumb.value = low;
      } else {
        high = low;
        maxThumb.value = high;
      }
    }

    // Native thumbs travel between their own centres, so the fill does too.
    const span = this.bounds.max - this.bounds.min || 1;
    const fill = range.querySelector('.srp-range__fill');
    const at = (value) => (value - this.bounds.min) / span;
    fill.style.left = `calc(${THUMB_SIZE / 2}px + ${at(low)} * (100% - ${THUMB_SIZE}px))`;
    fill.style.right = `calc(${THUMB_SIZE / 2}px + ${1 - at(high)} * (100% - ${THUMB_SIZE}px))`;

    if (!keepInputs) {
      range.querySelector('.srp-range__number[data-bound="min"]').value = formatNumber(low);
      range.querySelector('.srp-range__number[data-bound="max"]').value = formatNumber(high);
    }

    minThumb.setAttribute('aria-valuetext', formatPrice(low));
    maxThumb.setAttribute('aria-valuetext', formatPrice(high));

    // Let the lower thumb sit on top when both are pushed to the right edge.
    minThumb.classList.toggle('is-front', low >= this.bounds.max - span * 0.05);

    range.querySelectorAll('.srp-range__bar').forEach((bar) => {
      const inRange = Number(bar.dataset.to) > low && Number(bar.dataset.from) < high;
      bar.classList.toggle('is-in-range', inRange);
    });
  }

  /**
   * Model Year: a row per year in stock, newest first, with its count once the
   * plugin sends per-year counts (dealersite-inventory #347). One year at a time:
   * the API filters years as a range, so 2024 and 2026 would also bring in 2025.
   */
  renderYearList() {
    const years = this.getYears();
    const yearMin = this.params.year_min ?? '';
    const yearMax = this.params.year_max ?? '';
    const hasYear = Boolean(yearMin || yearMax);
    const single = yearMin && yearMin === yearMax ? String(yearMin) : '';

    this.optionCount = years.length;
    if (years.length < 2 && !hasYear) {
      this.container.innerHTML = '';
      this.optionCount = 0;
      return;
    }

    const counts = {};
    if (Array.isArray(this.facet)) {
      this.facet.forEach((option) => {
        counts[Number.parseInt(option.value ?? option.key, 10)] = option.doc_count;
      });
    }

    const rows = [{ key: '', label: this.i18n.any, checked: !hasYear }];
    // A range from an older link or the used From / To selects: keep it listed.
    if (hasYear && !single) {
      rows.push({ key: 'range', label: rangeLabel(yearMin, yearMax, this.i18n), checked: true });
    }
    years.forEach((year) => {
      rows.push({
        key: String(year),
        label: String(year),
        count: counts[year],
        checked: single === String(year),
      });
    });

    this.container.innerHTML = `<div class="srp-options">${this.optionListHtml(rows, {
      inputType: 'radio',
      name: 'srp_year',
    })}</div>`;
  }

  renderYearChips() {
    const years = this.getYears();
    this.optionCount = years.length;
    if (years.length < 2 && !this.params.year_min) {
      this.container.innerHTML = '';
      this.optionCount = 0;
      return;
    }

    const yearMin = this.params.year_min ?? '';
    const yearMax = this.params.year_max ?? '';
    const single = yearMin && yearMin === yearMax ? String(yearMin) : '';
    const anyChecked = !yearMin && !yearMax;

    const chip = (value, label, checked) => `<label class="srp-chip">
        <input type="radio" class="srp-chip__input" name="srp_year" value="${value}"${checked ? ' checked' : ''}>
        <span class="srp-chip__label">${escapeHtml(label)}</span>
      </label>`;

    let html = `<div class="srp-chips" role="radiogroup" aria-label="${escapeHtml(this.label)}">`;
    html += chip('', this.i18n.any, anyChecked);
    for (const year of years) {
      html += chip(year, year, single === String(year));
    }
    html += '</div>';

    this.container.innerHTML = html;
  }

  renderYearRange() {
    const years = this.getYears();
    this.optionCount = years.length;
    if (years.length < 2 && !this.params.year_min && !this.params.year_max) {
      this.container.innerHTML = '';
      this.optionCount = 0;
      return;
    }

    const select = (bound, label, current) => {
      const options = years
        .map(
          (year) =>
            `<option value="${year}"${String(current) === String(year) ? ' selected' : ''}>${year}</option>`,
        )
        .join('');
      return `<label class="srp-select">
          <span class="srp-select__label">${escapeHtml(label)}</span>
          <select class="srp-select__input" data-bound="${bound}">
            <option value="">${escapeHtml(this.i18n.any)}</option>${options}
          </select>
        </label>`;
    };

    this.container.innerHTML = `<div class="srp-select-range">
        ${select('min', this.i18n.from, this.params.year_min ?? '')}
        ${select('max', this.i18n.to, this.params.year_max ?? '')}
      </div>`;
  }

  getYears() {
    const years = [];

    // Years in stock for the other filters (dealersite-inventory #347), newest
    // first. Without them, every year between the site-wide min and max.
    if (Array.isArray(this.facet)) {
      this.facet
        .filter((option) => option.doc_count > 0)
        .map((option) => Number.parseInt(option.value ?? option.key, 10))
        .filter((year) => !Number.isNaN(year))
        .sort((a, b) => b - a)
        .forEach((year) => years.push(year));
    } else {
      const min = Number.parseInt(this.data.minYear, 10);
      const max = Number.parseInt(this.data.maxYear, 10);
      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        for (let year = max; year >= min; year--) {
          years.push(year);
        }
      }
    }

    // A selected year can fall outside the data when other filters narrow it.
    for (const value of [this.params.year_min, this.params.year_max]) {
      const year = Number.parseInt(value, 10);
      if (!Number.isNaN(year) && !years.includes(year)) {
        years.push(year);
      }
    }

    return years.sort((a, b) => b - a);
  }

  /**
   * "Under X km" bands. The API only reports the highest odometer bucket, so
   * the bands stop at the first one that covers all of the inventory.
   */
  getOdometerSteps() {
    const buckets = Array.isArray(this.data.odometer) ? this.data.odometer : [];
    const highest = buckets.length ? Number(buckets[buckets.length - 1].value) : 0;
    const steps = [];
    for (const step of ODOMETER_STEPS) {
      steps.push(step);
      if (highest && step >= highest) {
        break;
      }
    }
    return highest ? steps : [];
  }

  renderOdometer() {
    const current = this.params.odometer_max ?? '';
    const steps = this.getOdometerSteps();
    if (current && !steps.includes(Number(current))) {
      steps.push(Number(current));
      steps.sort((a, b) => a - b);
    }

    this.optionCount = steps.length;
    if (!this.optionCount) {
      this.container.innerHTML = '';
      return;
    }

    let html = '<ul class="srp-options__list" role="list">';
    html += this.optionRow({
      inputType: 'radio',
      name: 'srp_odometer',
      key: '',
      label: this.i18n.any,
      checked: !current,
    });
    for (const step of steps) {
      html += this.optionRow({
        inputType: 'radio',
        name: 'srp_odometer',
        key: String(step),
        label: sprintf(this.i18n.under, `${formatNumber(step)} ${this.i18n.km}`),
        checked: String(current) === String(step),
      });
    }
    html += '</ul>';

    this.container.innerHTML = `<div class="srp-options">${html}</div>`;
  }

  /*
  |--------------------------------------------------------------------------
  | Changes
  |--------------------------------------------------------------------------
  */

  handleInput(target) {
    // Option and chip ticks wait briefly for more ticks; see COMMIT_DELAY.
    this.batchNext = target.matches('.srp-option__input, .srp-chip__input');

    if (target.matches('.srp-option__input')) {
      if (this.isDeals) {
        const param = target.dataset.param;
        if (param === 'price_modifier') {
          const values = Array.from(
            this.ownAll('.srp-option__input[data-param="price_modifier"]:checked'),
            (input) => input.value,
          );
          this.emit({ param, value: values.join(',') });
        } else {
          this.emit({ param, value: target.checked ? 'true' : '' });
        }
        return;
      }
      if (this.control === 'odometer') {
        this.emit({ min: null, max: target.value || null });
        return;
      }
      if (this.control === 'year-list') {
        if (target.value !== 'range') {
          this.emit({ min: target.value || null, max: target.value || null });
        }
        return;
      }
      if (target.type === 'radio') {
        this.emit({ value: target.value });
        return;
      }
      const values = Array.from(this.ownAll('.srp-option__input:checked'), (input) => input.value);
      this.emit({ value: values.join(',') });
      return;
    }

    if (target.matches('.srp-chip__input')) {
      this.emit({ min: target.value || null, max: target.value || null });
      return;
    }

    if (target.matches('.srp-select__input')) {
      const selects = this.container.querySelectorAll('.srp-select__input');
      let min = selects[0].value || null;
      let max = selects[1].value || null;
      if (min && max && Number(min) > Number(max)) {
        [min, max] = [max, min];
      }
      this.emit({ min, max });
      return;
    }

    // The slider commits when released; a typed price when the box loses focus
    // (or on Enter, or once typing pauses: see bindEvents).
    if (target.matches('.srp-range__thumb')) {
      const thumbs = this.container.querySelectorAll('.srp-range__thumb');
      this.commitPrice(Number(thumbs[0].value), Number(thumbs[1].value));
    } else if (target.matches('.srp-range__number')) {
      this.commitPriceInputs();
    }
  }

  /**
   * Typed prices, read as whole dollars: "$45,000", "45000" and "45k" all work.
   * An empty box means no limit.
   */
  readPriceInputs() {
    const inputs = this.container.querySelectorAll('.srp-range__number');
    return [...inputs].map((input) => {
      const text = input.value.toLowerCase();
      const amount = Number(text.replace(/[^\d.]/g, '')) || 0;
      return Math.round(/k\s*$/.test(text) ? amount * 1000 : amount);
    });
  }

  /**
   * Move the slider and histogram to the typed prices while typing, without
   * rewriting the boxes under the cursor.
   */
  previewPriceInputs() {
    const [low, high] = this.readPriceInputs();
    const thumbs = this.container.querySelectorAll('.srp-range__thumb');
    if (thumbs.length !== 2) {
      return;
    }
    thumbs[0].value = clamp(low || this.bounds.min, this.bounds);
    thumbs[1].value = clamp(high || this.bounds.max, this.bounds);
    this.syncRange(null, { keepInputs: true });
  }

  commitPriceInputs() {
    clearTimeout(this.priceTimer);
    const inputs = this.container.querySelectorAll('.srp-range__number');
    if (inputs.length === 2) {
      const [low, high] = this.readPriceInputs();
      this.commitPrice(low, high);
    }
  }

  commitPrice(lowValue, highValue) {
    let low = lowValue || this.bounds.min;
    let high = highValue || this.bounds.max;
    if (low > high) {
      [low, high] = [high, low];
    }
    // A value at the end of the range means "no limit", unless that end is the
    // current limit itself (bounds widened to fit a deep-linked price).
    const currentMin = Number(this.params[`${this.param}_min`]) || null;
    const currentMax = Number(this.params[`${this.param}_max`]) || null;
    const min = low > this.bounds.min || low === currentMin ? low : null;
    const max = high < this.bounds.max || high === currentMax ? high : null;
    // Enter, then leaving the box, would otherwise search twice for the same price.
    if (min === currentMin && max === currentMax) {
      this.syncRange();
      return;
    }
    this.emit({ min, max });
  }

  /**
   * Hand the change to the plugin. Ranges carry `min`/`max`, Deals carries the
   * `param` it changes, and everything else carries a comma separated `value`.
   */
  emit(detail) {
    const batch = Boolean(this.batchNext);
    this.batchNext = false;
    this.container.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { ...detail, batch } }));
  }

  /**
   * Clear every value of this filter (the "‹ Clear" link).
   */
  clear() {
    if (RANGE_PARAMS.includes(this.param)) {
      this.emit({ min: null, max: null });
    } else if (this.isDeals) {
      // Each plugin update starts its own search, so clear one parameter per
      // search; afterSearch() sends the next one.
      this.pendingClear = DEAL_PARAMS.filter((key) => this.params[key]);
      this.afterSearch();
    } else {
      this.emit({ value: '' });
    }
  }

  /**
   * Called once each search has finished and every filter has been redrawn.
   */
  afterSearch() {
    while (this.pendingClear?.length) {
      const param = this.pendingClear.shift();
      if (this.params[param]) {
        this.emit({ param, value: '' });
        return;
      }
    }
  }

  commit({ batch, ...detail }) {
    if (!this.pluginFilter) {
      return;
    }

    const previous = this.params[detail.param ?? this.param];

    if (RANGE_PARAMS.includes(this.param)) {
      this.params[`${this.param}_min`] = detail.min ?? undefined;
      this.params[`${this.param}_max`] = detail.max ?? undefined;
    } else {
      this.params[detail.param ?? this.param] = detail.value ?? '';
    }
    this.onCommit?.(this, detail, previous);

    // A change to a different filter (or Deals option) sends the waiting one
    // first, so each search carries every earlier choice.
    const key = `${this.param}:${detail.param ?? ''}`;
    if (pending && pending.key !== key) {
      flushPendingChange();
    }
    if (pending) {
      window.clearTimeout(pending.timer);
      pending = null;
    }

    if (batch) {
      pending = {
        control: this,
        detail,
        key,
        timer: window.setTimeout(flushPendingChange, COMMIT_DELAY),
      };
    } else {
      this.send(detail);
    }
  }

  /**
   * Hand a change to the plugin, which starts the search.
   */
  send(detail) {
    const filter = this.pluginFilter;
    this.container.setAttribute('aria-busy', 'true');
    this.section?.classList.add('is-loading');
    this.nestedBlock?.classList.add('is-loading');

    if (RANGE_PARAMS.includes(this.param)) {
      // The plugin's range handling reads min/max off the filter instance.
      filter.searchParameters[`${this.param}_min`] = detail.min ?? null;
      filter.searchParameters[`${this.param}_max`] = detail.max ?? null;
      filter.updateCallback(this.param);
    } else if (detail.param) {
      // Deals: set the one parameter this option owns.
      filter.updateCallback(detail.param, detail.value ?? '');
    } else {
      filter.changeValue(detail.value ?? '');
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Section state, chips and focus
  |--------------------------------------------------------------------------
  */

  hasValue(params = this.params) {
    return this.ownKeys.some((key) =>
      RANGE_PARAMS.includes(this.param) ? Boolean(params[key]) : splitList(params[key]).length > 0,
    );
  }

  /**
   * Number of selected values, for the section and mobile group badges.
   */
  selectedCount() {
    if (RANGE_PARAMS.includes(this.param)) {
      return Number(this.hasValue());
    }
    if (this.isDeals) {
      return (
        Number(Boolean(this.params.on_sale)) +
        Number(Boolean(this.params.has_discounted_price)) +
        this.getSelected().length
      );
    }
    return this.getSelected().length;
  }

  /**
   * Show, hide and badge the surrounding section. A section with a reveal rule
   * stays hidden until its parent filter (e.g. model for trim) has a value.
   */
  updateSection() {
    if (!this.section) {
      return;
    }

    // A nested filter shows inside its parent, never in its own section.
    if (this.parentControl) {
      this.section.hidden = true;
      this.section.classList.remove('is-loading');
      this.nestedBlock.classList.remove('is-loading');
      return;
    }

    const revealed = !this.reveal || splitList(this.params[this.reveal]).length > 0;
    const hiddenBy = this.countsUnfiltered && !this.showCounts();
    const hasValue = this.hasValue();
    // A filter with a value always shows, so it can be cleared.
    const show = hasValue || (revealed && !hiddenBy && this.optionCount >= this.minOptions);

    if (this.section.hidden === show) {
      this.section.hidden = !show;
      if (show && this.reveal) {
        this.section.classList.add('is-revealed');
      }
    }

    this.section.classList.remove('is-loading');
    this.section.classList.toggle('has-value', hasValue);

    if (this.clearButton) {
      this.clearButton.hidden = !hasValue;
    }

    const badge = this.section.querySelector('[data-filter-badge]');
    if (badge) {
      const count = this.selectedCount();
      badge.textContent = count ? String(count) : '';
      badge.hidden = !count;
    }
  }

  /**
   * Active filter chips for this control: [{ label, remove }].
   */
  chips() {
    if (!this.hasValue()) {
      return [];
    }

    const min = this.params[`${this.param}_min`];
    const max = this.params[`${this.param}_max`];

    switch (this.param) {
      case 'price': {
        const label = rangeLabel(min && formatPrice(min), max && formatPrice(max), this.i18n);
        return [{ label, remove: () => this.emit({ min: null, max: null }) }];
      }
      case 'year': {
        const label = min && min === max ? String(min) : rangeLabel(min, max, this.i18n);
        return [{ label, remove: () => this.emit({ min: null, max: null }) }];
      }
      case 'odometer': {
        const label = max
          ? sprintf(this.i18n.under, `${formatNumber(max)} ${this.i18n.km}`)
          : rangeLabel(min, max, this.i18n);
        return [{ label, remove: () => this.emit({ min: null, max: null }) }];
      }
    }

    const selected = this.getSelected();
    const chips = selected.map((value) => ({
      label: this.labelFor(value),
      remove: () => {
        const rest = selected.filter((item) => item !== value).join(',');
        this.emit(this.isDeals ? { param: 'price_modifier', value: rest } : { value: rest });
      },
    }));

    if (this.isDeals) {
      if (this.params.on_sale) {
        chips.unshift({
          label: this.i18n.salePrice,
          remove: () => this.emit({ param: 'on_sale', value: '' }),
        });
      }
      if (this.params.has_discounted_price) {
        chips.push({
          label: this.i18n.anySavings,
          remove: () => this.emit({ param: 'has_discounted_price', value: '' }),
        });
      }
    }

    return chips;
  }

  /**
   * Display name for a selected value, e.g. "f-150" > "F-150".
   */
  labelFor(value) {
    const param = this.isDeals ? 'price_modifier' : this.param;
    const raw = Array.isArray(this.facet) ? this.facet : [];
    const option = raw.find((item) => isSelected(param, item, [value]));
    return option ? optionLabel(param, option) : value.replace(/-/g, ' ');
  }

  getFocusKey() {
    const active = document.activeElement;
    if (!active || !this.container.contains(active) || !this.owns(active)) {
      return null;
    }
    if (active.matches('.srp-options__search')) {
      return { selector: '.srp-options__search' };
    }
    if (active.matches('.srp-options__more')) {
      return { selector: '.srp-options__more' };
    }
    if (active.matches('[data-bound]')) {
      return { selector: `.${active.classList[0]}[data-bound="${active.dataset.bound}"]` };
    }
    if (active.matches('input')) {
      const name = CSS.escape(active.name);
      return { selector: `input[name="${name}"][value="${CSS.escape(active.value)}"]` };
    }
    return null;
  }

  restoreFocus(focusKey) {
    if (!focusKey) {
      return;
    }
    this.own(focusKey.selector)?.focus({ preventScroll: true });
  }
}

function clamp(value, { min, max }) {
  return Math.min(Math.max(value, min), max);
}

function rangeLabel(min, max, i18n) {
  if (min && max) {
    return `${min} – ${max}`;
  }
  if (min) {
    return sprintf(i18n.andUp, min);
  }
  return sprintf(i18n.under, max);
}
